import { Modal, ModalBody, ModalContent, ModalHeader, ModalTitle } from '@/common-utils/components/ui/modal';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import PropTypes from 'prop-types';
import { useToast } from '@/common-utils/hooks/use-toast';
import actionsPayluddSubscriptions from '../../api/actionsPayluddSubscriptions';
import { nsPayluddSubscription } from '@/app/i18n/namespaces';
import payluddSubscriptionFields from '../../fields/payluddSubscriptionFields';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import { isPayluddAppVersionAtLeast } from '@/common-utils/functions/comparePayluddAppVersion';

const PAYLUDD_AUTO_RESET_MIN_VERSION = '1.33.0';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.payluddSubscription
 * @param root0.setLaunchRefetch
 * @returns {JSX.Element} ModalTerminatePayluddSubscription Component
 */
function ModalTerminatePayluddSubscription({ isOpen, setIsOpen, payluddSubscription, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // API Calls
    const submitTerminatePayluddSubscription = actionsPayluddSubscriptions.terminatePayluddSubscription(() => { }, () => { });

    // Constants
    const subscriptionId = getObjectAttributeFromStringKey(
        payluddSubscription,
        generateNestedFieldAccessor(payluddSubscriptionFields.POS_TERMINAL_SUBSCRIPTION.UUID)
    );
    const payluddAppVersion = getObjectAttributeFromStringKey(
        payluddSubscription,
        generateNestedFieldAccessor(payluddSubscriptionFields.VERSION.PAYLUDD)
    );
    const supportsAutoReset = isPayluddAppVersionAtLeast(payluddAppVersion, PAYLUDD_AUTO_RESET_MIN_VERSION);
    const versionSpecificMessageKey = supportsAutoReset
        ? 'modal.terminate_payludd.text_from_1_33_0'
        : 'modal.terminate_payludd.text_before_1_33_0';

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-lg')}>
                <ModalHeader>
                    <ModalTitle className='font-normal'>
                        <Entity namespace={nsPayluddSubscription} entity={'modal.terminate_payludd.title'} />
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <div className='flex flex-col gap-4 text-center'>
                        <Entity
                            entity={'modal.terminate_payludd.text_common'}
                            namespace={nsPayluddSubscription}
                            className='font-bold'
                        />
                        <Entity
                            entity={versionSpecificMessageKey}
                            namespace={nsPayluddSubscription}
                            className='whitespace-pre-line'
                        />
                    </div>
                </ModalBody>
                <FooterForm
                    onSubmit={async () => {
                        try {
                            await submitTerminatePayluddSubscription.mutateAsync({ subscriptionId });
                            setLaunchRefetch(true);
                            toast({
                                description: <Entity entity={'modal.terminate_payludd.success'} namespace={nsPayluddSubscription} />,
                                variant: 'success'
                            });
                            setIsOpen(false);
                            // eslint-disable-next-line no-unused-vars
                        } catch (error) {
                            toast({
                                description: <Entity entity={'modal.terminate_payludd.error'} namespace={nsPayluddSubscription} />,
                                variant: 'danger'
                            });
                            setIsOpen(false);
                        }
                    }}
                    isSubmitting={submitTerminatePayluddSubscription.isLoading}
                    isSubmitDisabled={submitTerminatePayluddSubscription.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

ModalTerminatePayluddSubscription.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    payluddSubscription: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ModalTerminatePayluddSubscription;
